import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { api, errorMsg, getJson, putJson } from "../api";
import PcbUploadFields from "./PcbUploadFields";

async function putFormData(url, formData) {
  const res = await api.put(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
}

function boolFromBackend(value) {
  if (value === true || value === false) return value;
  return false;
}

function existingFileName(url) {
  if (!url) return "";
  try {
    const clean = String(url).split("?")[0];
    return decodeURIComponent(clean.split("/").pop() || "");
  } catch (_) {
    return String(url);
  }
}

function filePresent(payload, field) {
  return !!(payload?.[`${field}_present`] || payload?.[field] || payload?.[`${field}_name`]);
}

const StepPcbProvide = forwardRef(function StepPcbProvide({ projectId, disabled, onSuccess, onModeChange }, ref) {
  const [loading, setLoading] = useState(false);
  const [booting, setBooting] = useState(true);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [format, setFormat] = useState("gerber");

  const [asm, setAsm] = useState({ smd_side: "" });
  const [existingFiles, setExistingFiles] = useState({});
  const [includeInArchive, setIncludeInArchive] = useState({
    pick_place_in_archive: false,
    assembly_plan_in_archive: false,
    paste_top_in_archive: false,
    paste_bot_in_archive: false,
  });
  const [provideFiles, setProvideFiles] = useState({
    gerber_zip: null,
    odb_archive: null,
    pick_place_file: null,
    assembly_plan_file: null,
    placement_file: null,
    paste_top: null,
    paste_bot: null,
    notes: "",
  });

  useEffect(() => {
    let mounted = true;

    async function boot() {
      setBooting(true);
      try {
        const [cfg, filesRes] = await Promise.all([
          getJson(`/user/projects/${projectId}/pcb-config/`).catch(() => ({})),
          getJson(`/user/projects/${projectId}/customer-pcb-files/`).catch(() => ({})),
        ]);

        if (!mounted) return;

        const asmSpec = cfg?.assembly_spec || {};
        setAsm({
          smd_side: asmSpec?.smd_side || "",
        });

        const saved = filesRes?.customer_pcb_files || {};
        setExistingFiles(saved);
        setIncludeInArchive({
          pick_place_in_archive: boolFromBackend(saved.pick_place_in_archive),
          assembly_plan_in_archive: boolFromBackend(saved.assembly_plan_in_archive),
          paste_top_in_archive: boolFromBackend(saved.paste_top_in_archive),
          paste_bot_in_archive: boolFromBackend(saved.paste_bot_in_archive),
        });
        setProvideFiles((prev) => ({ ...prev, notes: saved.notes || "" }));

        const hasOdb = filePresent(saved, "odb_archive");
        const hasGerber = filePresent(saved, "gerber_zip");
        setFormat(hasOdb && !hasGerber ? "odb" : "gerber");
      } catch (e) {
        if (mounted) setError(errorMsg(e, "Impossible de charger les fichiers de production actuels."));
      } finally {
        if (mounted) setBooting(false);
      }
    }

    boot();
    return () => {
      mounted = false;
    };
  }, [projectId]);

  function existingName(field) {
    const directName = String(existingFiles?.[`${field}_name`] || "").trim();
    return directName || existingFileName(existingFiles?.[field]);
  }

  function setFile(field, file) {
    setProvideFiles((prev) => ({ ...prev, [field]: file }));
    if (file) {
      setExistingFiles((prev) => ({
        ...prev,
        [field]: null,
        [`${field}_name`]: "",
        [`${field}_present`]: false,
      }));
    }
  }

  function clearFile(field) {
    setProvideFiles((prev) => ({ ...prev, [field]: null }));
    setExistingFiles((prev) => ({
      ...prev,
      [field]: null,
      [`${field}_name`]: "",
      [`${field}_present`]: false,
    }));
  }

  function handleFormatChange(nextFormat) {
    setFormat(nextFormat);
    setError("");
    setOk("");
  }

  function confirmArchiveFlag(field, nextValue) {
    if (!nextValue) {
      setIncludeInArchive((prev) => ({ ...prev, [field]: false }));
      return;
    }

    const okConfirm = window.confirm("Êtes-vous sûr que ce fichier est bien inclus dans votre archive Gerber ?");
    if (!okConfirm) return;
    setIncludeInArchive((prev) => ({ ...prev, [field]: true }));
  }

  const effectivePresence = useMemo(() => {
    const includeAllSupportFiles = format === "odb";
    const hasArchive = format === "odb"
      ? !!(provideFiles.odb_archive || filePresent(existingFiles, "odb_archive"))
      : !!(provideFiles.gerber_zip || filePresent(existingFiles, "gerber_zip"));

    return {
      hasArchive,
      hasPickPlace: !!(
        includeAllSupportFiles ||
        provideFiles.pick_place_file ||
        provideFiles.placement_file ||
        filePresent(existingFiles, "pick_place_file") ||
        filePresent(existingFiles, "placement_file") ||
        includeInArchive.pick_place_in_archive
      ),
      hasAssemblyPlan: !!(
        includeAllSupportFiles ||
        provideFiles.assembly_plan_file ||
        provideFiles.placement_file ||
        filePresent(existingFiles, "assembly_plan_file") ||
        filePresent(existingFiles, "placement_file") ||
        includeInArchive.assembly_plan_in_archive
      ),
      hasPasteTop: !!(
        includeAllSupportFiles ||
        provideFiles.paste_top ||
        filePresent(existingFiles, "paste_top") ||
        includeInArchive.paste_top_in_archive
      ),
      hasPasteBot: !!(
        includeAllSupportFiles ||
        provideFiles.paste_bot ||
        filePresent(existingFiles, "paste_bot") ||
        includeInArchive.paste_bot_in_archive
      ),
    };
  }, [provideFiles, existingFiles, includeInArchive, format]);

  const canContinue = () => {
    if (!effectivePresence.hasArchive) return false;
    if (!asm.smd_side) return false;
    if (format === "odb") return true;
    if (!effectivePresence.hasPickPlace) return false;
    if (!effectivePresence.hasAssemblyPlan) return false;
    if (asm.smd_side === "top" && !effectivePresence.hasPasteTop) return false;
    if (asm.smd_side === "bottom" && !effectivePresence.hasPasteBot) return false;
    if (asm.smd_side === "both" && (!effectivePresence.hasPasteTop || !effectivePresence.hasPasteBot)) return false;
    return true;
  };

  const uploadProvideFiles = async () => {
    if (!effectivePresence.hasArchive) {
      throw new Error(format === "odb" ? "Veuillez fournir une archive ODB++." : "Veuillez fournir un Gerber ZIP.");
    }

    const selectedArchive = format === "odb" ? provideFiles.odb_archive : provideFiles.gerber_zip;
    if (format === "gerber" && selectedArchive && !selectedArchive.name.toLowerCase().endsWith(".zip")) {
      throw new Error("Le fichier Gerber doit être un .zip");
    }
    if (
      format === "odb" &&
      selectedArchive &&
      !selectedArchive.name.toLowerCase().endsWith(".zip") &&
      !selectedArchive.name.toLowerCase().endsWith(".tgz") &&
      !selectedArchive.name.toLowerCase().endsWith(".tar.gz")
    ) {
      throw new Error("Le fichier ODB++ doit être un .zip ou .tgz");
    }

    if (format !== "odb" && !effectivePresence.hasPickPlace) {
      throw new Error("Veuillez fournir le fichier pick and place ou confirmer qu'il est déjà dans l'archive.");
    }
    if (format !== "odb" && !effectivePresence.hasAssemblyPlan) {
      throw new Error("Veuillez fournir le plan d'implantation ou confirmer qu'il est déjà dans l'archive.");
    }
    if (format !== "odb" && asm.smd_side === "top" && !effectivePresence.hasPasteTop) {
      throw new Error("Veuillez fournir PASTE TOP ou confirmer qu'il est déjà dans l'archive.");
    }
    if (format !== "odb" && asm.smd_side === "bottom" && !effectivePresence.hasPasteBot) {
      throw new Error("Veuillez fournir PASTE BOT ou confirmer qu'il est déjà dans l'archive.");
    }
    if (format !== "odb" && asm.smd_side === "both" && (!effectivePresence.hasPasteTop || !effectivePresence.hasPasteBot)) {
      throw new Error("Veuillez fournir PASTE TOP et PASTE BOT, ou confirmer leur présence dans l'archive.");
    }

    const fd = new FormData();

    if (provideFiles.gerber_zip) fd.append("gerber_zip", provideFiles.gerber_zip);
    if (provideFiles.odb_archive) fd.append("odb_archive", provideFiles.odb_archive);

    if (format !== "odb") {
      if (provideFiles.pick_place_file) fd.append("pick_place_file", provideFiles.pick_place_file);
      if (provideFiles.assembly_plan_file) fd.append("assembly_plan_file", provideFiles.assembly_plan_file);
      if (provideFiles.placement_file) fd.append("placement_file", provideFiles.placement_file);
      if (provideFiles.paste_top) fd.append("paste_top", provideFiles.paste_top);
      if (provideFiles.paste_bot) fd.append("paste_bot", provideFiles.paste_bot);
    }

    fd.append("pick_place_in_archive", String(format === "odb" ? true : includeInArchive.pick_place_in_archive));
    fd.append("assembly_plan_in_archive", String(format === "odb" ? true : includeInArchive.assembly_plan_in_archive));
    fd.append("paste_top_in_archive", String(format === "odb" ? true : includeInArchive.paste_top_in_archive));
    fd.append("paste_bot_in_archive", String(format === "odb" ? true : includeInArchive.paste_bot_in_archive));
    fd.append("notes", provideFiles.notes.trim());

    const response = await putFormData(`/user/projects/${projectId}/customer-pcb-files/`, fd);
    setExistingFiles(response?.customer_pcb_files || {});
    return response;
  };

  const submit = async () => {
    setError("");
    setOk("");
    setLoading(true);

    try {
      onModeChange?.("provide");

      const payload = {
        pcb_spec: {
          client_pcb_mode: "provide",
          selected_choices: {},
        },
        assembly_spec: {
          customer_supplies_pcb: true,
          smd_side: asm.smd_side || null,
          press_fit: false,
        },
      };

      const data = await putJson(`/user/projects/${projectId}/pcb-config/`, payload);
      await uploadProvideFiles();

      setOk("Fichiers PCB envoyés et configuration enregistrée.");
      onSuccess?.(data, { mode: "provide" });
      return data;
    } catch (e) {
      setError(errorMsg(e, "Erreur enregistrement PCB (provide)"));
      throw e;
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({ submit, canContinue }));

  const disabledAll = !!disabled || loading || booting;

  return (
    <div style={{ marginTop: 14 }}>
      {error ? <div className="alert error" style={{ marginTop: 12 }}>{error}</div> : null}
      {ok ? <div className="alert ok" style={{ marginTop: 12 }}>{ok}</div> : null}

      <div className="form-row" style={{ marginTop: 12 }}>
        <label className="label">Faces CMS à assembler</label>
        <select disabled={disabledAll} value={asm.smd_side} onChange={(e) => setAsm((prev) => ({ ...prev, smd_side: e.target.value }))}>
          <option value="">-- Choisir --</option>
          <option value="top">CMS top</option>
          <option value="bottom">CMS bottom</option>
          <option value="both">CMS top + bottom</option>
        </select>
      </div>

      <PcbUploadFields
        format={format}
        disabled={disabledAll}
        onFormatChange={handleFormatChange}
        formatInputName="provide-format"
        archiveFile={format === "gerber" ? provideFiles.gerber_zip : provideFiles.odb_archive}
        archiveExistingName={format === "gerber" ? existingName("gerber_zip") : existingName("odb_archive")}
        onArchivePick={(file) => setFile(format === "gerber" ? "gerber_zip" : "odb_archive", file)}
        onArchiveClear={() => clearFile(format === "gerber" ? "gerber_zip" : "odb_archive")}
        supportFiles={provideFiles}
        getExistingName={existingName}
        onSupportFilePick={setFile}
        onSupportFileClear={clearFile}
        includeInArchive={includeInArchive}
        onArchiveToggle={confirmArchiveFlag}
        notes={provideFiles.notes}
        onNotesChange={(notes) => setProvideFiles((prev) => ({ ...prev, notes }))}
      />
    </div>
  );
});

export default StepPcbProvide;
